// Holds on the client (app.md §9). One place for the active-hold state every
// decision screen reads, and the one sentence that describes a hold.
//
// A HOLD STOPS AN INVOICE WHERE IT SITS. Whoever may decide at the invoice's
// step may place one or release one; the server refuses every decision that
// would move it while one is active, and accepts every edit (owner,
// 2026-10-03 — D-282: "Holds just prevent invoices from moving to the next
// step. Editable fields remain editable."). Since 2026-09-25 (owner) every modal step
// offers Hold, so every decision screen reads holds the same way — through the
// hook below rather than a copy of it per screen.
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ActiveHold, Invoice } from './types'
import { closeSentence, formatDate } from './format'
import { reasonCategoryLabel } from './constants'

/** "On hold — Dana Director on 09/25/2026: Awaiting Vendor Response — “Asked for
    a corrected invoice.”" The category and the reason each appear only when
    the hold has one; a hold placed before 2026-09-25 has no category. */
export function holdSummary(hold: ActiveHold): string {
  const who = hold.held_by_name || hold.held_by_email || 'unknown'
  const when = formatDate(hold.held_at)
  const category = hold.reason_category ? reasonCategoryLabel(hold.reason_category) : ''
  const reason = hold.reason?.trim() ? `“${hold.reason.trim()}”` : ''
  const why = [category, reason].filter(Boolean).join(' — ')
  return `On hold — ${who} on ${when}${why ? `: ${why}` : ''}`
}

/** The queue card's pill on a held invoice (`InvoiceQueue`). */
export const HELD_PILL = 'On hold'

/** The Import Control editor's notice on a held invoice, and the reason its
    Release and Archive are disabled. ONE full stop after the summary (QA
    2026-10-01, D1-03): a reason that ends in its own stop, as the Reason
    placeholder does, read *"… a corrected copy.”. Release the hold …"*.
    It does not say "edit": a held invoice stays editable (D-282). */
export function heldEditorNotice(hold: ActiveHold, forward = 'release'): string {
  return `${closeSentence(holdSummary(hold))} Release the hold to ${forward} or archive this invoice.`
}

/** Why an action is disabled on a held invoice — every screen says it the same
    way. */
export function heldReason(hold: ActiveHold): string {
  return `${holdSummary(hold)} — release the hold to act.`
}

/** Why a held row's checkbox is disabled, on every queue with bulk actions. */
export function heldSelectionReason(hold: ActiveHold): string {
  return `${holdSummary(hold)} — held invoices are excluded from bulk actions.`
}

/** The Release Hold button's tooltip (`HoldControl`). */
export function releaseHoldTitle(hold: ActiveHold, decidable: boolean): string {
  return decidable
    ? `${holdSummary(hold)} — release it to act on this invoice.`
    : `${holdSummary(hold)} — you're not authorized to release it.`
}

/** THE ACCESSIBLE NAME OF A DECISION BUTTON, AND WHY IT IS DISABLED (QA C2-04,
    2026-09-25). One rule for every action a hold can disable — the row icons
    and the modal buttons at every step:

    - The NAME is the button's own label, said once — "Approve", "Deny",
      "Release". A held invoice's Approve used to be named by its tooltip alone
      ("On hold — … — release the hold to act."), with no "Approve" in it.
    - A HOLD is the DESCRIPTION: `aria-description`, which a screen reader
      reads after the name, as the `title` tooltip shows it on hover. The hold
      is a state of the whole invoice — the On hold pill, the notice and the
      Release Hold button all say it — so it describes the button rather than
      renaming it.
    - ANY OTHER REASON (the coding gate, a flag, authority) stays folded into
      the name, "Approve — Finish the coding…", as the 2026-08-30 walk and QA
      C1-01 put it: that reason is what the reader has to fix, and an
      icon-only row button has nothing else to say it with.

    The Hold / Release Hold control itself is simpler still: its name is its
    label and its tooltip, whatever it says, is its description
    (`HoldControl`).

    `held` is the hold sentence (`heldReason`, or the editor's frozen notice);
    `reason` any other. Spread the result onto the `Button`. */
export function actionA11y(
  label: string,
  why: { held?: string; reason?: string } = {},
): { 'aria-label': string; 'aria-description'?: string } {
  if (why.held) return { 'aria-label': label, 'aria-description': why.held }
  if (why.reason && why.reason !== label) return { 'aria-label': `${label} — ${why.reason}` }
  return { 'aria-label': label }
}

/** The Hold dialog's one sentence (owner, 2026-09-25) — true at every step
    that places a hold (Import Control, the tiers, Export Control and, since
    D-327, Denial Review). */
export const HOLD_BODY =
  'This invoice will stay at this step, and no one can act on it until someone releases the hold.'

/** An example reason, in the placeholder — never a description of the control. */
export const HOLD_REASON_PLACEHOLDER = 'Waiting on the vendor for a corrected invoice.'

/** The Release Hold dialog's one sentence, naming the hold it releases. */
export function releaseHoldBody(hold: ActiveHold): string {
  const who = hold.held_by_name || hold.held_by_email || 'unknown'
  return `The hold placed by ${who} on ${formatDate(hold.held_at)} will be released, and this invoice can be acted on again.`
}

/**
 * A hold this screen placed or released, against the row object it was made on
 * (`useActiveHolds().setHoldFor`). The queue row carries its active hold
 * (`Invoice.active_hold`, D-284); until the queue re-reads, the row still says
 * what it said before the click, so the mark answers for that row object and
 * only that one. A fresh read is a new object and decides for itself, so a
 * hold placed in another tab shows on the next read whatever this screen did.
 */
export interface HoldMark {
  row: object
  hold: ActiveHold | null
}

/** The row's active hold as this screen knows it now, pure. */
export function activeHoldOf(
  inv: Pick<Invoice, 'id' | 'active_hold'> | null | undefined,
  marks: ReadonlyMap<string, HoldMark>,
): ActiveHold | undefined {
  if (!inv) return undefined
  const mark = marks.get(inv.id)
  if (mark && mark.row === inv) return mark.hold ?? undefined
  return inv.active_hold ?? undefined
}

/**
 * THE IDS A QUEUE TREATS AS HELD: dimmed, the `On hold` pill, its row actions
 * disabled, no checkbox, and at Export Control out of the group's pills and its
 * Export (D-263). The same answer as `activeHoldOf`.
 */
export function heldIdSet(
  rows: readonly Pick<Invoice, 'id' | 'active_hold'>[],
  marks: ReadonlyMap<string, HoldMark>,
): Set<string> {
  return new Set(rows.filter((row) => activeHoldOf(row, marks) !== undefined).map((row) => row.id))
}

/**
 * The active hold per loaded row, read off the row itself (D-284).
 *
 * `setHoldFor` applies a hold the screen has just placed or released without
 * waiting for the queue to reload, so the open modal shows the new state at
 * once; the reload that follows brings the same answer from the server.
 */
export function useActiveHolds(rows: readonly Invoice[]) {
  const [marks, setMarks] = useState<ReadonlyMap<string, HoldMark>>(() => new Map())
  // The latest rows, so `setHoldFor` (a stable callback) can find the row a
  // hold is placed or released against.
  const rowsRef = useRef(rows)
  useEffect(() => {
    rowsRef.current = rows
    // A mark whose row is no longer loaded can never apply again: drop it.
    const loaded = new Set<object>(rows)
    setMarks((prev) => {
      const kept = [...prev].filter(([, mark]) => loaded.has(mark.row))
      return kept.length === prev.size ? prev : new Map(kept)
    })
  }, [rows])
  /** The row's active hold. Pass the LIVE row (the objects this hook was
      given, or a filter of them), never a copy taken when a modal opened. */
  const holdFor = useCallback(
    (inv: Pick<Invoice, 'id' | 'active_hold'> | null | undefined): ActiveHold | undefined => activeHoldOf(inv, marks),
    [marks],
  )
  const isHeld = useCallback(
    (inv: Pick<Invoice, 'id' | 'active_hold'> | null | undefined): boolean => activeHoldOf(inv, marks) !== undefined,
    [marks],
  )
  /** `heldIdSet` over the rows a screen shows. */
  const heldIdsIn = useCallback(
    (shown: readonly Invoice[]): Set<string> => heldIdSet(shown, marks),
    [marks],
  )
  const setHoldFor = useCallback((invoiceId: string, hold: ActiveHold | null) => {
    const row = rowsRef.current.find((i) => i.id === invoiceId)
    if (!row) return
    setMarks((prev) => new Map(prev).set(invoiceId, { row, hold }))
  }, [])
  return { holdFor, isHeld, heldIdsIn, setHoldFor }
}
